import React, { forwardRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, type BoxProps } from '@atlaskit/primitives/compiled/box';
import useTabPanel from '@atlaskit/tabs/use-tab-panel';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		overflow: 'hidden',
		paddingBottom: token('space.075'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.150'),
	},
	rootCollapsed: {
		height: '120px',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	rootCollapsedWithReadMore: {
		height: '92px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	rootExpanded: {
		// @ts-expect-error - TS2322 - workaround to fix TabPanel's overly-weighted specificity for minHeight styling
		minHeight: '120px !important', // eslint-disable-line @atlaskit/ui-styling-standard/no-important-styles
	},
	fullWidth: {
		width: '100%',
	},
});

type AITabsPanelProps = {
	children: React.ReactNode;
	collapsed?: boolean;
	showReadMoreButton?: boolean;
	xcss?: BoxProps<'div'>['xcss'];
	id?: string;
};

export const AITabsPanel: React.ForwardRefExoticComponent<
	AITabsPanelProps & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, AITabsPanelProps>(
	({ children, collapsed, showReadMoreButton = false, xcss }, ref) => {
		const tabPanelAttributes = useTabPanel();

		return (
			<Box
				{...tabPanelAttributes}
				ref={ref}
				xcss={
					xcss ??
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					cx(
						styles.root,
						collapsed
							? showReadMoreButton
								? styles.rootCollapsedWithReadMore
								: styles.rootCollapsed
							: styles.rootExpanded,
					)
				}
			>
				{/* use nested Box to prevent tab panel's flex row layout from affecting children */}
				<Box xcss={styles.fullWidth}>{children}</Box>
			</Box>
		);
	},
);
